import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Disc3, Edit2, CheckCircle2, Clock, Save, X, Search, Plus,
  Trash2, ExternalLink, Users, Music, ArrowRight, BarChart2,
  Shield, Bell, Check, Send, Loader2, Pencil, Globe, MessageCircle,
  Instagram, Facebook, Twitter, Mail, Building2, ToggleLeft,
  ToggleRight, Power, Phone, ChevronDown, ChevronUp, Info, Palette,
  Eye, EyeOff, XCircle, Home, TrendingUp, Zap, Star, Heart,
} from "lucide-react";
import ImageUploadField from "../components/ImageUploadField";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";
import { toast } from "sonner";

const EQ_HEIGHTS = [38, 68, 52, 82, 47, 72, 44];

// ── Marketing Features (für Nicht-Manager) ────────────────────────────────────
// defined inside component so t() works

// ── Artist Search (für Roster) ────────────────────────────────────────────────
function ArtistSearchAdd({ token, onAdded, labelId }) {
  const { t } = useTranslation();
  const [q, setQ]            = useState("");
  const [results, setResults] = useState([]);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const res = await api.get(`/artists/?search=${encodeURIComponent(q)}&limit=8`);
      setResults(res.data.items || res.data.artists || []);
    } catch {}
  };

  const add = async (artist) => {
    try {
      await api.post("/labels/studio/my/roster", {
        artist_id: artist.id || artist.slug,
        target_label_id: labelId || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("labels.studio.add_success", { name: artist.name }));
      setQ(""); setResults([]);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
            placeholder={t("labels.studio.artist_search_ph")}
            className="w-full pl-9 pr-4 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
        </div>
        <button onClick={search} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-sm transition-colors">
          <Search size={14} />
        </button>
      </div>
      {results.length > 0 && (
        <div className="space-y-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          {results.map(a => (
            <button key={a.id || a.slug} onClick={() => add(a)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors text-left">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-700 flex-shrink-0">
                {a.bild_url ? <img src={a.bild_url} alt="" className="w-full h-full object-cover" />
                  : <span className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-400">{a.name?.[0]}</span>}
              </div>
              <span className="text-zinc-200">{a.name}</span>
              <Plus size={13} className="ml-auto text-zinc-500" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Label-Anfragen Formular ────────────────────────────────────────────────────
const LABEL_REQ_LF = ["Remixer", "Vocalist", "Co-Producer", "Songwriter", "Mixing/Mastering", "Andere"];

function LabelReqForm({ token, initialData, onSaved, onCancel }) {
  const { t } = useTranslation();
  const isEdit = !!initialData?.id;
  const inp = "w-full bg-zinc-900/60 border border-white/[0.07] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50 transition-colors";
  const [form, setForm] = useState({
    active:      initialData?.active      ?? true,
    headline:    initialData?.headline    || "",
    looking_for: initialData?.looking_for || [],
    style:       initialData?.style       || "",
    deadline:    initialData?.deadline    || "",
    description: initialData?.description || "",
    contact: {
      email:     initialData?.contact?.email     || "",
      instagram: initialData?.contact?.instagram || "",
      telegram:  initialData?.contact?.telegram  || "",
      phone:     initialData?.contact?.phone     || "",
    },
  });
  const [saving, setSaving] = useState(false);
  const toggleLF = (v) => setForm(f => ({
    ...f,
    looking_for: f.looking_for.includes(v) ? f.looking_for.filter(x => x !== v) : [...f.looking_for, v],
  }));
  const hasContact = form.contact.email || form.contact.instagram || form.contact.telegram || form.contact.phone;

  const save = async () => {
    if (!form.headline.trim()) { toast.error(t("label_req.err_headline")); return; }
    if (!hasContact)           { toast.error(t("label_req.err_contact"));  return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/label-requests/mine/${initialData.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.post("/label-requests/mine", form, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t("label_req.toast_saved"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("label_req.err_generic")); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: "linear-gradient(160deg,#071a12 0%,#091f15 60%,#050e09 100%)", border: "1px solid rgba(16,185,129,0.25)", boxShadow: "0 16px 48px rgba(0,0,0,0.5)" }}>
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(16,185,129,0.1)" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.2)" }}>
            <Building2 size={15} className="text-emerald-400" />
          </div>
          <p className="text-white font-bold text-sm">{isEdit ? t("label_req.form_edit_title") : t("label_req.form_create_title")}</p>
        </div>
        <button onClick={onCancel} className="text-zinc-600 hover:text-zinc-400 transition-colors"><X size={16} /></button>
      </div>
      <div className="px-6 pb-6 space-y-5 pt-5">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-semibold">{t("label_req.form_active_label")}</p>
            <p className="text-zinc-500 text-xs mt-0.5">{t("label_req.form_active_desc")}</p>
          </div>
          <button onClick={() => setForm(f => ({ ...f, active: !f.active }))} data-testid="label-req-active-toggle">
            {form.active ? <ToggleRight size={32} className="text-emerald-400" /> : <ToggleLeft size={32} className="text-zinc-600" />}
          </button>
        </div>
        {/* Headline */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_headline_label")}</label>
          <input data-testid="label-req-headline" maxLength={100} value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder={t("label_req.form_headline_ph")} className={inp} />
          <p className="text-zinc-700 text-xs text-right">{form.headline.length}/100</p>
        </div>
        {/* Gesucht */}
        <div className="space-y-2">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_looking_for_label")}</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {LABEL_REQ_LF.map(opt => (
              <button key={opt} onClick={() => toggleLF(opt)} data-testid={`label-lf-${opt.toLowerCase()}`}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={form.looking_for.includes(opt)
                  ? { background: "rgba(16,185,129,0.2)", border: "1px solid rgba(16,185,129,0.4)", color: "#6ee7b7" }
                  : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "#71717a" }}>
                {opt}
              </button>
            ))}
          </div>
        </div>
        {/* Stil */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_style_label")}</label>
          <input data-testid="label-req-style" maxLength={200} value={form.style} onChange={e => setForm(f => ({ ...f, style: e.target.value }))} placeholder="z.B. Hands Up, Hard Trance" className={inp} />
        </div>
        {/* Deadline */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_deadline_label")}</label>
          <input data-testid="label-req-deadline" maxLength={80} value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} placeholder={t("label_req.form_deadline_ph")} className={inp} />
        </div>
        {/* Beschreibung */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_desc_label")}</label>
          <textarea data-testid="label-req-desc" maxLength={800} rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t("label_req.form_desc_ph")} className={inp + " resize-none"} />
          <p className="text-zinc-700 text-xs text-right">{form.description.length}/800</p>
        </div>
        {/* Kontakt */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("label_req.form_contact_label")}</label>
            <span className="text-xs text-zinc-600 normal-case font-normal">{t("label_req.form_contact_min")}</span>
          </div>
          <div className="rounded-xl p-3 space-y-2.5" style={{ background: "rgba(16,185,129,0.04)", border: "1px solid rgba(16,185,129,0.08)" }}>
            {[
              { icon: Mail,      key: "email",     type: "email", ph: "E-Mail",                max: 120 },
              { icon: Instagram, key: "instagram", type: "text",  ph: "Instagram @handle",     max: 80  },
              { icon: Send,      key: "telegram",  type: "text",  ph: "Telegram @handle",      max: 80  },
              { icon: Phone,     key: "phone",     type: "text",  ph: "Telefon / WhatsApp",    max: 40  },
            ].map(({ icon: Icon, key, type, ph, max }) => (
              <div key={key} className="flex items-center gap-2.5">
                <Icon size={13} className="text-zinc-500 flex-shrink-0" />
                <input data-testid={`label-req-contact-${key}`} type={type} maxLength={max} value={form.contact[key]}
                  onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, [key]: e.target.value } }))}
                  placeholder={ph}
                  className="flex-1 bg-transparent border-b border-white/[0.07] pb-1 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40 transition-colors" />
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 text-xs text-zinc-600">
            <Info size={11} className="flex-shrink-0 mt-0.5" />
            {t("label_req.form_contact_hint")}
          </div>
        </div>
        {/* Buttons */}
        <div className="flex gap-3 pt-1">
          <button onClick={save} disabled={saving} data-testid="label-req-save-btn"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(110deg,#059669,#047857)", border: "1px solid rgba(16,185,129,0.4)" }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {t("label_req.form_save_btn")}
          </button>
          <button onClick={onCancel} disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-400 hover:text-zinc-200 transition-all border border-white/[0.06]">
            <X size={14} /> {t("label_req.form_cancel_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vorschau-Karte (so wie Artists die Anfrage im Collab Finder sehen) ────────
function LabelReqPreviewCard({ req, label }) {
  return (
    <div className="rounded-2xl overflow-hidden pointer-events-none select-none opacity-90"
      style={{ background: "linear-gradient(160deg, rgba(16,185,129,0.06) 0%, rgba(5,150,105,0.03) 100%)", border: "1px solid rgba(16,185,129,0.2)" }}>
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
          style={{ background: "rgba(16,185,129,0.12)", color: "#34d399", border: "1px solid rgba(16,185,129,0.2)" }}>
          <Building2 size={9} /> Auftrag von {req.label_name || label?.name || "–"}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)" }}>
            {(req.label_logo || label?.logo_url)
              ? <img src={req.label_logo || label?.logo_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><Building2 size={18} className="text-emerald-600" /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">{req.label_name || label?.name || "–"}</p>
            <p className="text-zinc-300 text-xs mt-0.5 font-medium">{req.headline || "Headline…"}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(req.looking_for || []).map(lf => (
            <span key={lf} className="px-2.5 py-1 rounded-full text-[10px] font-bold"
              style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", color: "#34d399" }}>
              {lf}
            </span>
          ))}
        </div>
        {req.style && (
          <div className="flex items-center gap-1.5 mb-2">
            <Palette size={10} className="text-zinc-600" />
            <p className="text-zinc-500 text-[11px]"><span className="text-zinc-600 font-semibold">Stil:</span> {req.style}</p>
          </div>
        )}
        {req.deadline && (
          <div className="flex items-center gap-1.5 mb-2">
            <Clock size={10} className="text-zinc-600" />
            <p className="text-zinc-500 text-[11px]"><span className="text-zinc-600 font-semibold">Deadline:</span> {req.deadline}</p>
          </div>
        )}
        {req.description && <p className="text-zinc-500 text-xs leading-relaxed mb-3">{req.description}</p>}
        <div className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "#71717a" }}>
          <ChevronDown size={12} /> Kontakt anzeigen
        </div>
      </div>
    </div>
  );
}

function LabelRequestsSection({ token, label }) {

  const { t } = useTranslation();
  const [reqs,       setReqs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [previewId,  setPreviewId]  = useState(null); // id der aufgeklappten Vorschau

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/label-requests/mine", { headers: { Authorization: `Bearer ${token}` } });
      setReqs(Array.isArray(res.data) ? res.data : []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("label_req.form_delete_confirm"))) return;
    setDeleting(id);
    try {
      await api.delete(`/label-requests/mine/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("label_req.toast_deleted"));
      setReqs(prev => prev.filter(r => r.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || t("label_req.err_generic")); }
    setDeleting(null);
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.patch(`/label-requests/mine/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.active ? t("label_req.toast_on") : t("label_req.toast_off"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("label_req.err_generic")); }
  };

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(16,185,129,0.15)", background: "rgba(16,185,129,0.02)" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "rgba(16,185,129,0.1)" }}>
        <div className="flex items-center gap-3">
          <Building2 size={14} className="text-emerald-400" />
          <p className="text-white font-bold text-sm">{t("label_req.my_section_title")}</p>
          {reqs.length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>{reqs.length}</span>
          )}
        </div>
        {!showForm && (
          <button onClick={() => { setEditTarget(null); setShowForm(true); }}
            data-testid="label-req-new-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all"
            style={{ background: "linear-gradient(110deg,#059669,#047857)", border: "1px solid rgba(16,185,129,0.4)" }}>
            <Plus size={12} /> {t("label_req.new_btn")}
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">

            {/* ── Info-Box (immer sichtbar wenn kein Formular offen) ── */}
        {!showForm && (
          <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.12)" }}>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <Info size={13} className="text-emerald-400" />
              </div>
              <div className="space-y-2">
                <p className="text-white text-sm font-bold">{t("label_req.info_title")}</p>
                <p className="text-zinc-400 text-xs leading-relaxed">{t("label_req.info_desc")}</p>
                {/* Kontakt-Hinweis */}
                <div className="flex items-start gap-2">
                  <Send size={10} className="text-emerald-500/60 flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-500 text-xs">{t("label_req.info_contact_note")}</p>
                </div>
                {/* Intern-Hinweis */}
                <div className="flex items-start gap-2">
                  <Shield size={10} className="text-emerald-500/60 flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-500 text-xs">{t("label_req.info_internal_note")}</p>
                </div>
              </div>
            </div>
            {/* Keine-Demo-Hinweis */}
            <div className="flex items-start gap-2 pt-1 border-t" style={{ borderColor: "rgba(16,185,129,0.1)" }}>
              <XCircle size={12} className="text-amber-500/70 flex-shrink-0 mt-0.5" />
              <p className="text-zinc-500 text-xs">{t("label_req.no_demo_note")}</p>
            </div>
          </div>
        )}

        {/* ── Formular ── */}
        {showForm && (
          <LabelReqForm token={token} initialData={editTarget}
            onSaved={() => { setShowForm(false); setEditTarget(null); load(); }}
            onCancel={() => { setShowForm(false); setEditTarget(null); }} />
        )}

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-zinc-600" /></div>
        ) : reqs.length === 0 && !showForm ? (
          <div className="text-center py-4">
            <p className="text-zinc-600 text-sm">{t("label_req.no_requests")}</p>
            <p className="text-zinc-700 text-xs mt-1">{t("label_req.no_requests_hint")}</p>
          </div>
        ) : (
          reqs.map(req => (
            <div key={req.id}>
              {/* Verwaltungszeile */}
              <div data-testid={`label-req-row-${req.id}`}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-sm font-semibold truncate max-w-xs">{req.headline || "—"}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
                      style={req.active
                        ? { background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", color: "#34d399" }
                        : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#52525b" }}>
                      {req.active ? t("label_req.active_badge") : t("label_req.inactive_badge")}
                    </span>
                  </div>
                  {req.looking_for?.length > 0 && (
                    <p className="text-zinc-600 text-xs mt-0.5">{req.looking_for.join(" · ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Vorschau-Button */}
                  <button
                    onClick={() => setPreviewId(previewId === req.id ? null : req.id)}
                    data-testid={`label-req-preview-${req.id}`}
                    title={previewId === req.id ? t("label_req.preview_hide") : t("label_req.preview_btn")}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all"
                    style={previewId === req.id
                      ? { background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", color: "#34d399" }
                      : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "#52525b" }}>
                    {previewId === req.id
                      ? <><EyeOff size={11} /> {t("label_req.preview_hide")}</>
                      : <><Eye size={11} /> {t("label_req.preview_btn")}</>}
                  </button>
                  <button onClick={() => handleToggle(req.id)} data-testid={`label-req-toggle-${req.id}`}
                    title={t("collab.toggle_btn")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all">
                    <Power size={13} />
                  </button>
                  <button onClick={() => { setEditTarget(req); setShowForm(true); }} data-testid={`label-req-edit-${req.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.06] transition-all">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(req.id)} disabled={deleting === req.id} data-testid={`label-req-delete-${req.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-40">
                    {deleting === req.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              </div>

              {/* Vorschau-Karte: genau wie Artists es sehen */}
              {previewId === req.id && (
                <div className="mt-2 ml-2 mr-2">
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                    <Eye size={9} /> {t("label_req.preview_btn")} – Artist Studio → Label-Aufträge
                  </p>
                  <LabelReqPreviewCard req={req} label={label} />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Bewerbungsformular ────────────────────────────────────────────────────────
function ApplicationForm({ token, existingApps, onSubmitted }) {
  const { t } = useTranslation();
  const [labelSearch, setLabelSearch] = useState("");
  const [labelResults, setLabelResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [form, setForm] = useState({ label_name: "", contact_email: "", info_text: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const hasPending = existingApps.some(a => a.status === "pending");

  const searchLabels = async () => {
    if (!labelSearch.trim()) return;
    try {
      const res = await api.get(`/labels/?search=${encodeURIComponent(labelSearch)}&limit=8`);
      setLabelResults(res.data.labels || []);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label_name.trim() || !form.contact_email.trim() || !form.info_text.trim()) {
      toast.error(t("labels.studio.form_validation"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/label-applications", {
        ...form,
        label_id: selectedLabel?.id || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("labels.studio.form_success"));
      onSubmitted();
      setForm({ label_name: "", contact_email: "", info_text: "", website: "" });
      setSelectedLabel(null); setLabelSearch("");
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
    setSubmitting(false);
  };

  if (hasPending) return (
    <div className="text-center py-8">
      <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
        style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}>
        <Clock size={20} className="text-emerald-400" />
      </div>
      <p className="text-white font-bold mb-1">{t("labels.studio.form_pending_title")}</p>
      <p className="text-zinc-500 text-sm">{t("labels.studio.form_pending_desc")}</p>
    </div>
  );

  const inp2 = "w-full px-3 py-2.5 text-sm rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 transition";
  const inpStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" };

  return (
    <form ref={formRef} onSubmit={submit} className="space-y-4">
      {/* Label suchen */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block">{t("labels.studio.form_search_label")}</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input value={labelSearch} onChange={e => setLabelSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), searchLabels())}
              placeholder={t("labels.studio.form_search_ph")}
              className={`${inp2} pl-9`} style={inpStyle} />
          </div>
          <button type="button" onClick={searchLabels}
            className="px-3 py-2 rounded-xl text-zinc-400 hover:text-white transition-colors text-sm" style={inpStyle}>
            <Search size={14} />
          </button>
        </div>
        {selectedLabel && (
          <div className="flex items-center justify-between px-3 py-2 rounded-xl"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
            <span className="text-emerald-400 text-sm font-semibold">{selectedLabel.name}</span>
            <button type="button" onClick={() => { setSelectedLabel(null); setForm(f => ({ ...f, label_name: "" })); }}
              className="text-zinc-500 hover:text-white"><X size={13} /></button>
          </div>
        )}
        {labelResults.length > 0 && !selectedLabel && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            {labelResults.map(l => (
              <button key={l.id} type="button"
                onClick={() => { setSelectedLabel(l); setForm(f => ({ ...f, label_name: l.name })); setLabelResults([]); setLabelSearch(""); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors text-left">
                {l.logo_url ? <img src={l.logo_url} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                  : <div className="w-7 h-7 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0"><Disc3 size={12} className="text-zinc-400" /></div>}
                <span className="text-zinc-200">{l.name}</span>
                <Check size={13} className="ml-auto text-zinc-600" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">{t("labels.studio.form_label_name")} <span className="text-rose-400">*</span></label>
        <input value={form.label_name} onChange={e => setForm(f => ({ ...f, label_name: e.target.value }))}
          placeholder={t("labels.studio.form_label_name_ph")} className={inp2} style={inpStyle} />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">{t("labels.studio.form_email")} <span className="text-rose-400">*</span></label>
        <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
          placeholder="deine@email.de" className={inp2} style={inpStyle} />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">{t("labels.studio.form_relation")} <span className="text-rose-400">*</span></label>
        <textarea value={form.info_text} onChange={e => setForm(f => ({ ...f, info_text: e.target.value.slice(0, 1000) }))}
          rows={3} placeholder={t("labels.studio.form_relation_ph")}
          className={`${inp2} resize-none`} style={inpStyle} />
        <p className="text-right text-zinc-700 text-xs mt-0.5">{form.info_text.length}/1000</p>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">{t("labels.studio.form_website")}</label>
        <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
          placeholder="https://..." className={inp2} style={inpStyle} />
      </div>

      <button type="submit" disabled={submitting}
        className="w-full py-3 rounded-xl text-white font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(110deg, rgba(16,185,129,0.8), rgba(5,150,105,0.9))", boxShadow: "0 2px 0 rgba(255,255,255,0.06) inset" }}>
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        {submitting ? t("labels.studio.form_submitting") : t("labels.studio.form_submit")}
      </button>
    </form>
  );
}

// ── Label-Benachrichtigungen (für Artists im Label) ───────────────────────────
function LabelLinkNotifications({ token }) {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const fetchLinks = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get("/labels/artist/my-label-links", { headers: { Authorization: `Bearer ${token}` } });
      // Zeige nur noch nicht bestätigte Links (status accepted aber notified=false)
      setLinks((res.data || []).filter(l => l.status === "accepted" && !l.notified));
    } catch {}
  }, [token]);
  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const respond = async (linkId, action) => {
    try {
      await api.put(`/labels/artist/my-label-links/${linkId}`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      if (action === "report") toast.success(t("labels.studio.notif_reported"));
      fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
  };

  if (links.length === 0) return null;
  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.15)" }}>
      <div className="flex items-center gap-3 px-5 py-3.5 border-b" style={{ borderColor: "rgba(245,158,11,0.1)" }}>
        <Bell size={14} className="text-amber-400" />
        <p className="text-white font-bold text-sm">{t("labels.studio.notif_title")}</p>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {links.map(l => (
          <div key={l.link_id} className="flex items-center gap-4 px-5 py-3.5">
            <div className="flex-1 min-w-0">
              <a href={`/label/${l.label_slug}`} target="_blank" rel="noreferrer"
                className="text-sm font-semibold text-white hover:text-emerald-400 transition-colors">{l.label_name}</a>
              <p className="text-xs text-zinc-500 mt-0.5">{t("labels.studio.notif_added")}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => respond(l.link_id, "dismiss")}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/[0.06] hover:bg-white/10 text-zinc-300 transition-colors">
                <Check size={11} /> {t("labels.studio.notif_dismiss")}
              </button>
              <button onClick={() => respond(l.link_id, "report")}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-colors">
                <X size={11} /> {t("labels.studio.notif_report")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Label Release Submit Form ─────────────────────────────────────────────────
function LabelReleaseSubmitForm({ labelName, publicApi, onSuccess }) {
  const { t } = useTranslation();
  const RELEASE_TYPES = ["Single", "EP", "Album", "Compilation", "Remix"];
  const [form, setForm] = useState({
    interpret: "", titel: "", label: labelName || "",
    erscheinungsdatum: "", cover_url: "", typ: "Single",
    buy_links: { beatport: "", spotify: "", apple_music: "", amazon: "" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const setBuy = (key, val) => setForm(p => ({ ...p, buy_links: { ...p.buy_links, [key]: val } }));

  const submit = async () => {
    if (!form.interpret.trim() || !form.titel.trim()) { setError(t("labels.studio.release_error")); return; }
    setSubmitting(true); setError("");
    try {
      await publicApi("post", "/release-proposals/", {
        ...form,
        buy_links: Object.fromEntries(Object.entries(form.buy_links).filter(([, v]) => v.trim())),
      });
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Einreichen.");
    } finally { setSubmitting(false); }
  };

  const inpCls = "input-studio";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_artist")} *</label>
          <input value={form.interpret} onChange={e => set("interpret", e.target.value)} placeholder="Artist-Name" className={inpCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_title_lbl")} *</label>
          <input value={form.titel} onChange={e => set("titel", e.target.value)} placeholder="Release-Titel" className={inpCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_label_lbl")}</label>
          <input value={form.label} onChange={e => set("label", e.target.value)} placeholder="Label-Name" className={inpCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_type")}</label>
          <select value={form.typ} onChange={e => set("typ", e.target.value)} className={inpCls}>
            {RELEASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_date")}</label>
          <input type="date" value={form.erscheinungsdatum} onChange={e => set("erscheinungsdatum", e.target.value)} className={inpCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("labels.studio.field_cover")}</label>
          <input value={form.cover_url} onChange={e => set("cover_url", e.target.value)} placeholder="https://..." className={inpCls} />
        </div>
      </div>

      {form.cover_url && (
        <div className="flex items-center gap-3">
          <img src={form.cover_url} alt="Cover" className="w-12 h-12 rounded-xl object-cover ring-1 ring-white/10"
            onError={e => { e.target.style.display = "none"; }} />
          <span className="text-zinc-600 text-xs">{t("labels.studio.field_cover_preview")}</span>
        </div>
      )}

      <div>
        <label className="block text-xs text-zinc-500 mb-2">{t("labels.studio.field_buy_links")}</label>
        <div className="space-y-2">
          {[{ key: "beatport", label: "Beatport" }, { key: "spotify", label: "Spotify" },
            { key: "apple_music", label: "Apple Music" }, { key: "amazon", label: "Amazon" }]
            .map(({ key, label }) => (
              <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "5.5rem 1fr" }}>
                <span className="text-zinc-600 text-xs text-right">{label}</span>
                <input value={form.buy_links[key]} onChange={e => setBuy(key, e.target.value)}
                  placeholder="https://..." className={`${inpCls} text-xs`} />
              </div>
            ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button onClick={submit} disabled={submitting}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl disabled:opacity-50 text-white text-sm font-bold transition"
        style={{ background: "linear-gradient(110deg, rgba(16,185,129,0.85), rgba(5,150,105,0.9))" }}>
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {submitting ? t("labels.studio.release_submitting") : t("labels.studio.release_submit")}
      </button>
      <p className="text-zinc-700 text-xs">{t("labels.studio.release_review_note")}</p>
    </div>
  );
}

// ── Label Release Submit Toggle ───────────────────────────────────────────────
function LabelReleaseSubmitToggle({ labelName, publicApi }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) return (
    <div className="rounded-xl bg-zinc-900 px-5 py-8 text-center">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3"
        style={{ background: "rgba(16,185,129,0.15)" }}>
        <Check size={18} className="text-emerald-400" />
      </div>
      <p className="text-white font-bold text-sm mb-1">{t("labels.studio.release_submitted_title")}</p>
      <p className="text-zinc-500 text-xs">{t("labels.studio.release_submitted_desc")}</p>
      <button onClick={() => setSubmitted(false)} className="mt-4 text-xs text-zinc-600 hover:text-white transition">
        {t("labels.studio.release_submit_another")}
      </button>
    </div>
  );

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full group relative rounded-xl overflow-hidden text-left"
      style={{
        background: "linear-gradient(110deg, #061a0e 0%, #0a2d18 55%, #061a0e 100%)",
        boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.3) inset, 0 4px 16px rgba(0,0,0,0.4)",
      }}
    >
      <div className="absolute right-5 inset-y-0 flex items-end gap-[3px] py-3 opacity-[0.18] group-hover:opacity-[0.35] transition-opacity duration-300">
        {EQ_HEIGHTS.map((h, i) => (<div key={i} className="w-[3px] rounded-full bg-emerald-400" style={{ height: `${h}%` }} />))}
      </div>
      <div className="relative flex items-center gap-4 px-5 py-4 pr-20">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(16,185,129,0.18)", boxShadow: "inset 0 1px 0 rgba(52,211,153,0.12)" }}>
          <Disc3 size={16} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight">{t("labels.studio.release_submit")}</p>
          <p className="text-zinc-500 text-xs mt-0.5">{t("labels.studio.release_submit_new", { name: labelName || "dein Label" })}</p>
        </div>
        <Plus size={14} className="text-zinc-700 group-hover:text-emerald-400 transition-colors flex-shrink-0" />
      </div>
    </button>
  );

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ background: "linear-gradient(180deg, #061a0e 0%, #040d07 50%, #030808 100%)", boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 4px 24px rgba(0,0,0,0.5)" }}
    >
      <div className="flex items-center justify-between px-5 py-3.5"
        style={{ borderBottom: "1px solid rgba(16,185,129,0.10)" }}>
        <div className="flex items-center gap-3">
          <Disc3 size={14} className="text-emerald-400" />
          <p className="text-white font-bold text-sm">{t("labels.studio.release_submit")}</p>
          {labelName && <span className="text-zinc-600 text-xs">· {labelName}</span>}
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-zinc-600 hover:text-white hover:bg-white/[0.04] transition">
          <X size={14} />
        </button>
      </div>
      <div className="px-5 py-5">
        <LabelReleaseSubmitForm labelName={labelName} publicApi={publicApi}
          onSuccess={() => { setOpen(false); setSubmitted(true); }} />
      </div>
    </div>
  );
}

// ── Edit Section ──────────────────────────────────────────────────────────────
function EditSection({ title, icon: Icon, iconColor, children, onSave, saving, subtitle }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border transition-all duration-300 ${
      open ? "border-emerald-500/20 bg-emerald-500/[0.02]" : "border-white/[0.05] bg-zinc-900/30 hover:border-white/10"
    }`}>
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.04)" }}>
              <Icon size={14} className={iconColor || "text-zinc-400"} />
            </div>
          )}
          <div>
            <p className="text-white font-semibold text-sm leading-tight">{title}</p>
            {subtitle && <p className="text-zinc-600 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <button onClick={() => setOpen(v => !v)}
          className={`text-xs px-3 py-1.5 rounded-xl font-semibold transition flex items-center gap-1.5 ${
            open ? "bg-zinc-800 text-zinc-400 hover:text-white" : "bg-white/[0.06] text-zinc-400 hover:bg-white/10 hover:text-white"
          }`}>
          {open ? <><X size={11} /> {t("labels.studio.close")}</> : <><Pencil size={11} /> {t("labels.studio.edit")}</>}
        </button>
      </div>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/[0.05] pt-4">
          {children}
          <button onClick={async () => { await onSave(); setOpen(false); }} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
            style={{ background: "rgba(16,185,129,0.7)" }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            {saving ? t("labels.studio.saving") : t("labels.studio.save")}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Label Card (Dashboard) ────────────────────────────────────────────────────
function LabelCard({ label, onEdit }) {
  const { t } = useTranslation();
  const stats = [
    { lbl: t("labels.studio.stat_releases"), val: label.release_count ?? 0,               icon: Disc3 },
    { lbl: t("labels.studio.stat_roster"),   val: label.roster?.length ?? 0,              icon: Users },
    { lbl: t("labels.studio.stat_manager"),  val: label.managers?.length ?? label.manager_ids?.length ?? 0, icon: Shield },
    { lbl: t("labels.studio.stat_top5"),     val: label.top_releases_data?.length ?? label.top_releases?.length ?? 0, icon: Music },
  ];

  return (
    <div className="rounded-2xl overflow-hidden flex flex-col sm:flex-row"
      style={{
        background: "linear-gradient(160deg, #061a0e 0%, #0a2d18 60%, #030e09 100%)",
        boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 8px 32px rgba(0,0,0,0.5)",
        border: "1px solid rgba(16,185,129,0.14)",
      }}>

      {/* Logo links */}
      <div className="relative w-full sm:w-48 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
        {label.logo_url
          ? <img src={label.logo_url} alt={label.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center" style={{ background: "rgba(3,14,9,0.85)" }}>
              <Disc3 size={40} className="text-emerald-900/40" />
            </div>
        }
        <div className="absolute inset-0 sm:hidden"
          style={{ background: "linear-gradient(to bottom, transparent 50%, rgba(3,14,9,1) 100%)" }} />
        <div className="hidden sm:block absolute inset-0"
          style={{ background: "linear-gradient(to right, transparent 65%, rgba(3,14,9,0.9) 100%)" }} />
        <div className="hidden sm:flex absolute right-0 inset-y-0 items-end gap-[3px] pr-1 py-5 opacity-10">
          {[55,80,40,95,65,45,70,50].map((h, i) => (
            <div key={i} className="w-[3px] rounded-full bg-emerald-400" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      {/* Info rechts */}
      <div className="flex-1 flex flex-col p-5 -mt-8 sm:mt-0 relative z-10">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {(label.manager_ids?.length > 0 || label.managers?.length > 0) && (
              <span className="relative group cursor-help inline-flex">
                <span className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{
                    background: "linear-gradient(145deg, #34d399 0%, #10b981 45%, #059669 100%)",
                    boxShadow: "0 1px 0 rgba(255,255,255,0.32) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 3px 10px rgba(16,185,129,0.55), 0 1px 3px rgba(0,0,0,0.3)",
                  }}>
                  <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5">
                    <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-56 px-3 py-2 text-xs text-zinc-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-center z-50 leading-relaxed whitespace-normal tracking-normal"
                  style={{ background: "rgba(10,10,10,0.97)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                  {t("labels.tooltip_managed")}
                </span>
              </span>
            )}
          </div>
          <h2 className="text-white font-black text-xl leading-tight">{label.name}</h2>
          {(label.country || label.founded_year) && (
            <p className="text-zinc-500 text-xs mt-0.5 flex items-center gap-1">
              <Globe size={9} className="text-emerald-500/40" />
              {label.country}{label.country && label.founded_year ? " · " : ""}{label.founded_year ? t("labels.founded", { year: label.founded_year }) : ""}
            </p>
          )}
          {label.bio && (
            <p className="text-zinc-500 text-xs leading-relaxed mt-2 line-clamp-2">{label.bio}</p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-px rounded-xl overflow-hidden my-4"
          style={{ background: "rgba(16,185,129,0.07)" }}>
          {stats.map(({ lbl, val, icon: Icon }) => (
            <div key={lbl} className="flex flex-col items-center justify-center py-2.5 gap-0.5"
              style={{ background: "rgba(3,14,9,0.85)" }}>
              <span className="text-white font-black text-base leading-none">{val.toLocaleString("de")}</span>
              <span className="text-zinc-600 text-[9px] uppercase tracking-wider flex items-center gap-0.5">
                <Icon size={7} className="text-emerald-500/40" /> {lbl}
              </span>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <a href={`/label/${label.slug}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-zinc-500 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <ExternalLink size={11} /> {t("labels.studio.open_profile")}
          </a>
          <button onClick={onEdit}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:brightness-110"
            style={{
              background: "linear-gradient(110deg, rgba(16,185,129,0.25) 0%, rgba(16,185,129,0.12) 100%)",
              border: "1px solid rgba(16,185,129,0.25)",
              boxShadow: "0 1px 0 rgba(52,211,153,0.08) inset",
            }}>
            <Pencil size={11} /> {t("labels.studio.edit_profile")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LabelStudioPage() {
  const { t } = useTranslation();
  const { publicUser, token, publicApi } = usePublicAuth();
  const [labels, setLabels]             = useState([]);
  const [activeLabelIdx, setActiveLabelIdx] = useState(0);
  const [myApps, setMyApps]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState("overview");
  const [studioStats, setStudioStats]   = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingTop5, setSavingTop5]     = useState(false);
  const [top5, setTop5]                 = useState([]);
  const [top5Search, setTop5Search]     = useState("");
  const [top5Results, setTop5Results]   = useState([]);
  const [profileForm, setProfileForm]   = useState({});

  const label = labels[activeLabelIdx] || null;

  const fetchLabels = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get("/labels/studio/my", { headers: { Authorization: `Bearer ${token}` } });
      const data = Array.isArray(res.data) ? res.data : [res.data];
      setLabels(data);
      if (data[0]) {
        setProfileForm({
          bio: data[0].bio || "", logo_url: data[0].logo_url || "",
          banner_url: data[0].banner_url || "",
          founded_year: data[0].founded_year || "", country: data[0].country || "",
          contact_email: data[0].contact_email || "",
          social_links: {
            website:    data[0].social_links?.website    || "",
            instagram:  data[0].social_links?.instagram  || "",
            facebook:   data[0].social_links?.facebook   || "",
            x:          data[0].social_links?.x          || "",
            tiktok:     data[0].social_links?.tiktok     || "",
            beatport:   data[0].social_links?.beatport   || "",
            spotify:    data[0].social_links?.spotify    || "",
            soundcloud: data[0].social_links?.soundcloud || "",
            youtube:    data[0].social_links?.youtube    || "",
          },
        });
        setTop5(data[0].top_releases || []);
      }
    } catch {}
  }, [token]);

  const fetchApps = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get("/label-applications/my", { headers: { Authorization: `Bearer ${token}` } });
      setMyApps(res.data || []);
    } catch {}
  }, [token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoadingStats(true);
    try {
      const labelId = labels[activeLabelIdx]?.id;
      const params = labelId ? `?label_id=${labelId}` : "";
      const res = await api.get(`/labels/studio/my/stats${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudioStats(res.data);
    } catch {}
    setLoadingStats(false);
  }, [token, activeLabelIdx, labels]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchLabels(), fetchApps()]);
      setLoading(false);
    })();
  }, [fetchLabels, fetchApps]);

  // Statistiken laden wenn Stats-Tab geöffnet wird
  useEffect(() => {
    if (activeTab === "stats" && !studioStats && !loadingStats) {
      fetchStats();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Top5-Suche
  useEffect(() => {
    if (!top5Search.trim()) { setTop5Results([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await api.get(`/releases/?search=${encodeURIComponent(top5Search)}&limit=8`);
        setTop5Results(r.data?.items || []);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [top5Search]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/labels/studio/my", { ...profileForm, target_label_id: label?.id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("labels.studio.save_profile_success"));
      fetchLabels();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
    setSavingProfile(false);
  };

  const saveTop5 = async () => {
    setSavingTop5(true);
    try {
      await api.put("/labels/studio/my/top-releases", { release_ids: top5, target_label_id: label?.id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("labels.studio.save_top5_success"));
      fetchLabels();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
    setSavingTop5(false);
  };

  const removeFromRoster = async (artistId) => {
    try {
      const qs = label?.id ? `?label_id=${label.id}` : "";
      await api.delete(`/labels/studio/my/roster/${artistId}${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("labels.studio.remove_success"));
      fetchLabels();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
  };

  const handleSwitchLabel = (idx) => {
    setActiveLabelIdx(idx);
    const l = labels[idx];
    if (l) {
      setProfileForm({
        bio: l.bio || "", logo_url: l.logo_url || "",
        banner_url: l.banner_url || "",
        founded_year: l.founded_year || "", country: l.country || "",
        contact_email: l.contact_email || "",
        social_links: {
          website:    l.social_links?.website    || "",
          instagram:  l.social_links?.instagram  || "",
          facebook:   l.social_links?.facebook   || "",
          x:          l.social_links?.x          || "",
          tiktok:     l.social_links?.tiktok     || "",
          beatport:   l.social_links?.beatport   || "",
          spotify:    l.social_links?.spotify    || "",
          soundcloud: l.social_links?.soundcloud || "",
          youtube:    l.social_links?.youtube    || "",
        },
      });
      setTop5(l.top_releases || []);
      setStudioStats(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500/40 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const inp = "w-full px-3 py-2.5 text-sm bg-zinc-900/80 border border-zinc-800 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 transition";

  const NAV_ITEMS = [
    { id: "overview",     label: t("labels.studio.nav_overview"),     icon: Home },
    { id: "stats",        label: t("labels.studio.nav_stats"),        icon: BarChart2 },
    { id: "artists",      label: t("labels.studio.nav_artists"),      icon: Users },
    { id: "requests",     label: t("labels.studio.nav_requests"),     icon: Building2 },
    { id: "top-releases", label: t("labels.studio.nav_top_releases"), icon: Star },
    { id: "profile",      label: t("labels.studio.nav_profile"),      icon: Edit2 },
  ];

  return (
    <div className="min-h-screen bg-[#080808] flex flex-col">
      <Helmet><title>{`${t("labels.studio.badge")} | HUWND`}</title></Helmet>
      <Header />

      <main className={`flex-1 w-full ${label ? "max-w-5xl mx-auto px-4 py-8" : ""}`}>

        {/* ── OHNE Label: FuerLabels-Marketing + Bewerbungsformular ─────── */}
        {!label && (
          <>
            {/* Hero – zentriert, große Typografie */}
            <section className="max-w-4xl mx-auto px-4 py-12 sm:py-20 text-center space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", color: "#10b981" }}>
                <Disc3 size={11} /> {t("labels.studio.beta_badge")}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-none">
                {t("labels.fuer.hero_title")}<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-rose-600">
                  {t("labels.fuer.hero_subtitle")}
                </span>
              </h1>
              <p className="text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed">
                {t("labels.fuer.hero_desc")}
              </p>
              <a href="#bewerben"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-2xl transition-all hover:scale-[1.02] shadow-lg shadow-rose-900/30">
                {t("labels.fuer.apply_btn")} <ArrowRight size={16} />
              </a>
            </section>

            {/* Features Grid */}
            <section className="max-w-5xl mx-auto px-4 pb-16">
              <h2 className="text-center text-xs font-bold text-zinc-500 uppercase tracking-widest mb-8">
                {t("labels.fuer.what_you_get")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: <Disc3 size={18} className="text-emerald-400" />, title: t("labels.fuer.f1_t"), desc: t("labels.fuer.f1_d") },
                  { icon: <Music size={18} className="text-rose-400" />,    title: t("labels.fuer.f2_t"), desc: t("labels.fuer.f2_d") },
                  { icon: <Users size={18} className="text-blue-400" />,    title: t("labels.fuer.f3_t"), desc: t("labels.fuer.f3_d") },
                  { icon: <BarChart2 size={18} className="text-amber-400" />,  title: t("labels.fuer.f4_t"), desc: t("labels.fuer.f4_d") },
                  { icon: <Shield size={18} className="text-violet-400" />,    title: t("labels.fuer.f5_t"), desc: t("labels.fuer.f5_d") },
                  { icon: <CheckCircle2 size={18} className="text-zinc-300" />,title: t("labels.fuer.f6_t"), desc: t("labels.fuer.f6_d") },
                ].map(f => (
                  <div key={f.title} className="p-5 rounded-2xl bg-zinc-900/60 border border-white/[0.06] space-y-3 hover:border-white/10 transition-colors">
                    <div className="w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center">{f.icon}</div>
                    <p className="font-bold text-white text-sm">{f.title}</p>
                    <p className="text-zinc-500 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Label-Anfragen Spotlight ── */}
            <section className="max-w-5xl mx-auto px-4 pb-16">
              <div className="relative rounded-3xl overflow-hidden"
                style={{ background: "linear-gradient(135deg, #071a10 0%, #0a2416 50%, #061510 100%)", border: "1px solid rgba(16,185,129,0.25)" }}>
                {/* Glow */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-0 right-0 w-80 h-40 bg-emerald-500/8 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-60 h-32 bg-emerald-600/5 rounded-full blur-3xl" />
                </div>
                <div className="relative p-8 sm:p-10">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                    {/* Text */}
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.25)" }}>
                          <Building2 size={17} className="text-emerald-400" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-500/70">
                          {t("labels.fuer.f7_t")}
                        </span>
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                        {t("labels.fuer.req_title")}
                      </h3>
                      <p className="text-zinc-400 text-sm leading-relaxed max-w-lg">
                        {t("labels.fuer.req_desc")}
                      </p>
                      {/* Claim */}
                      <p className="text-emerald-400 text-sm font-bold italic">
                        „{t("labels.fuer.req_claim")}"
                      </p>
                      {/* Intern-Badge */}
                      <div className="flex items-center gap-2 pt-1">
                        <Shield size={12} className="text-emerald-600" />
                        <span className="text-zinc-600 text-xs">{t("labels.fuer.req_internal")}</span>
                      </div>
                    </div>
                    {/* Rechte Seite: Rollen-Chips + "Wo erscheint es?" */}
                    <div className="lg:w-72 space-y-5 flex-shrink-0">
                      {/* Rollen */}
                      <div className="flex flex-wrap gap-2">
                        {["Remixer", "Vocalist", "Co-Producer", "Songwriter", "Mixing/Mastering"].map(role => (
                          <span key={role}
                            className="px-3 py-1.5 rounded-full text-xs font-bold"
                            style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", color: "#34d399" }}>
                            {role}
                          </span>
                        ))}
                      </div>
                      {/* Artist Studio Breadcrumb */}
                      <div className="rounded-2xl p-4 space-y-2"
                        style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(16,185,129,0.1)" }}>
                        <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-semibold">
                          {t("labels.fuer.req_appears")}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-zinc-400">
                          <span className="px-2 py-1 rounded-lg text-[10px] font-bold"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)" }}>
                            Artist Studio
                          </span>
                          <ArrowRight size={10} className="text-zinc-600" />
                          <span className="px-2 py-1 rounded-lg text-[10px] font-bold"
                            style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)", color: "#34d399" }}>
                            Label-Aufträge
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Ablauf */}
            <section className="border-t border-white/[0.06] py-16">
              <div className="max-w-3xl mx-auto px-4">
                <h2 className="text-center text-xs font-bold text-zinc-500 uppercase tracking-widest mb-10">
                  {t("labels.fuer.how_it_works")}
                </h2>
                <div className="space-y-8">
                  {[
                    { n: "01", title: t("labels.fuer.step1_t"), desc: t("labels.fuer.step1_d") },
                    { n: "02", title: t("labels.fuer.step2_t"), desc: t("labels.fuer.step2_d") },
                    { n: "03", title: t("labels.fuer.step3_t"), desc: t("labels.fuer.step3_d") },
                  ].map(s => (
                    <div key={s.n} className="flex gap-5">
                      <span className="text-3xl font-black text-white/10 font-mono w-12 flex-shrink-0 pt-0.5">{s.n}</span>
                      <div className="space-y-1 pt-1">
                        <p className="font-bold text-white text-base">{s.title}</p>
                        <p className="text-zinc-500 text-sm leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Bewerbungsbereich */}
            <section id="bewerben" className="max-w-3xl mx-auto px-4 pb-16 space-y-6">

              {/* Bisherige Bewerbungen */}
              {myApps.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest">{t("labels.studio.my_apps")}</p>
                  {myApps.map(app => (
                    <div key={app.id} className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-semibold text-white text-sm">{app.label_name}</p>
                        <p className="text-xs text-zinc-500">{app.created_at ? new Date(app.created_at).toLocaleDateString() : ""}</p>
                      </div>
                      {app.status === "pending"  && <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20">{t("labels.studio.status_pending")}</span>}
                      {app.status === "approved" && <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">{t("labels.studio.status_approved")}</span>}
                      {app.status === "rejected" && (
                        <div className="text-right">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/20">{t("labels.studio.status_rejected")}</span>
                          {app.reviewer_note && <p className="text-xs text-zinc-500 mt-1">{app.reviewer_note}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Bewerbungsformular – Dark Gradient Card (grün) */}
              <div className="rounded-2xl overflow-hidden" style={{
                background: "linear-gradient(160deg, #051a10 0%, #092d1c 60%, #030e09 100%)",
                boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 16px 48px rgba(0,0,0,0.6)",
                border: "1px solid rgba(16,185,129,0.18)",
              }}>
                <div className="flex items-center gap-4 px-6 py-5 border-b" style={{ borderColor: "rgba(16,185,129,0.10)" }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.18)" }}>
                    <Disc3 size={18} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-base">{t("labels.studio.apply_title")}</p>
                    <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                      {token ? t("labels.studio.apply_desc_auth") : t("labels.studio.apply_desc_guest")}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  {token
                    ? <ApplicationForm token={token} existingApps={myApps} onSubmitted={fetchApps} />
                    : <div className="text-center py-6 space-y-4">
                        <p className="text-zinc-500 text-sm">{t("labels.studio.login_required")}</p>
                        <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold rounded-xl text-white transition-colors"
                          style={{ background: "rgba(16,185,129,0.7)" }}>
                          {t("labels.studio.login_btn")} <ArrowRight size={15} />
                        </Link>
                      </div>
                  }
                </div>
              </div>

            </section>
          </>
        )}

        {/* ── MIT Label: Sidebar-Layout ──────────────────────────────────── */}
        {label && (
          <>
            {/* Mobile: Label-Dropdown + Horizontal Tabs */}
            <div className="md:hidden mb-6 space-y-3">
              {labels.length > 1 && (
                <select
                  value={activeLabelIdx}
                  onChange={e => handleSwitchLabel(+e.target.value)}
                  data-testid="label-select-mobile"
                  className="w-full text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                >
                  {labels.map((l, idx) => (
                    <option key={l.id} value={idx}>{l.name}</option>
                  ))}
                </select>
              )}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {NAV_ITEMS.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`mobile-tab-${item.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                      activeTab === item.id ? "text-white" : "text-zinc-500 bg-zinc-900/60 hover:text-zinc-200"
                    }`}
                    style={activeTab === item.id ? {
                      background: "rgba(16,185,129,0.15)",
                      border: "1px solid rgba(16,185,129,0.25)",
                    } : {}}
                  >
                    <item.icon size={12} />{item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop: Sidebar + Content */}
            <div className="flex gap-8 items-start">

              {/* ── Sidebar ──────────────────────────────────────────────────── */}
              <aside className="hidden md:flex flex-col w-52 flex-shrink-0 sticky top-8">

                {/* Label Info */}
                <div className="mb-5 pb-5 border-b border-white/[0.06]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 flex-shrink-0 flex items-center justify-center">
                      {label.logo_url
                        ? <img src={label.logo_url} alt={label.name} className="w-full h-full object-cover" />
                        : <Disc3 size={16} className="text-zinc-600" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-white text-sm leading-tight truncate">{label.name}</p>
                      <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Label Studio</p>
                    </div>
                  </div>
                  {/* Label-Dropdown bei mehreren Labels */}
                  {labels.length > 1 && (
                    <select
                      value={activeLabelIdx}
                      onChange={e => handleSwitchLabel(+e.target.value)}
                      data-testid="label-select-desktop"
                      className="w-full text-xs bg-zinc-900 border border-zinc-700 text-zinc-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 cursor-pointer"
                    >
                      {labels.map((l, idx) => (
                        <option key={l.id} value={idx}>{l.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Nav */}
                <nav className="space-y-0.5">
                  {NAV_ITEMS.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      data-testid={`sidebar-nav-${item.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all text-left ${
                        activeTab === item.id
                          ? "text-white"
                          : "text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]"
                      }`}
                      style={activeTab === item.id ? {
                        background: "rgba(16,185,129,0.12)",
                        border: "1px solid rgba(16,185,129,0.18)",
                      } : {}}
                    >
                      <item.icon size={15} className={activeTab === item.id ? "text-emerald-400" : "text-zinc-600"} />
                      {item.label}
                    </button>
                  ))}
                </nav>

                {/* Public Profile Link */}
                <div className="mt-4 pt-4 border-t border-white/[0.05]">
                  <a
                    href={`/label/${label.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs text-zinc-600 hover:text-zinc-300 transition-colors"
                  >
                    <ExternalLink size={12} />{t("labels.studio.open_profile")}
                  </a>
                </div>
              </aside>

              {/* ── Content Area ─────────────────────────────────────────────── */}
              <div className="flex-1 min-w-0 space-y-6">

                {/* Label-Benachrichtigungen (immer sichtbar) */}
                <LabelLinkNotifications token={token} />

                {/* ── ÜBERSICHT ──────────────────────────────────────────────── */}
                {activeTab === "overview" && (
                  <div className="space-y-6">

                    {/* Welcome Message */}
                    <div className="relative rounded-2xl overflow-hidden px-6 py-6"
                      style={{
                        background: "linear-gradient(135deg, #061a0e 0%, #0a2d18 60%, #030e09 100%)",
                        border: "1px solid rgba(16,185,129,0.15)",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
                      }}>
                      <div className="absolute right-5 inset-y-0 flex items-end gap-[3px] pb-4 opacity-[0.12] pointer-events-none">
                        {EQ_HEIGHTS.map((h, i) => (
                          <div key={i} className="w-[3px] rounded-full bg-emerald-400" style={{ height: `${h}%` }} />
                        ))}
                      </div>
                      <div className="relative">
                        <p className="text-[10px] font-bold text-emerald-500/60 uppercase tracking-[0.2em] mb-2">
                          Label Studio
                        </p>
                        <h2 className="text-2xl font-black text-white leading-tight mb-1">
                          {t("labels.studio.welcome_greeting")}{publicUser?.username ? `, ${publicUser.username}` : ""}!
                        </h2>
                        <p className="text-zinc-400 text-sm mb-5">
                          {t("labels.studio.welcome_label_prefix")}: <span className="text-emerald-400 font-bold">{label.name}</span>
                          {label.founded_year && <span className="text-zinc-600"> · {t("labels.founded", { year: label.founded_year })}</span>}
                        </p>
                        {/* Quick Stats */}
                        <div className="flex gap-6">
                          {[
                            { val: label.release_count ?? 0, lbl: t("labels.studio.stat_releases"), icon: Disc3 },
                            { val: label.roster?.length ?? 0, lbl: t("labels.studio.stat_roster"), icon: Users },
                            { val: label.managers?.length ?? label.manager_ids?.length ?? 0, lbl: t("labels.studio.stat_manager"), icon: Shield },
                          ].map(s => (
                            <div key={s.lbl} className="text-center">
                              <p className="text-2xl font-black text-white leading-none">{s.val.toLocaleString("de")}</p>
                              <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5 flex items-center gap-0.5 justify-center">
                                <s.icon size={8} className="text-emerald-500/40" /> {s.lbl}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Label Card */}
                    <LabelCard label={label} onEdit={() => setActiveTab("profile")} />

                    {/* Quick-Action Tiles */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "stats",        icon: BarChart2, label: t("labels.studio.nav_stats"),        accent: "#fbbf24", bg: "rgba(245,158,11,0.06)",  border: "rgba(245,158,11,0.15)" },
                        { id: "artists",      icon: Users,     label: t("labels.studio.nav_artists"),      accent: "#60a5fa", bg: "rgba(59,130,246,0.06)", border: "rgba(59,130,246,0.15)" },
                        { id: "requests",     icon: Building2, label: t("labels.studio.nav_requests"),     accent: "#34d399", bg: "rgba(16,185,129,0.06)", border: "rgba(16,185,129,0.15)" },
                        { id: "top-releases", icon: Star,      label: t("labels.studio.nav_top_releases"), accent: "#f87171", bg: "rgba(244,63,94,0.06)",  border: "rgba(244,63,94,0.15)" },
                      ].map(item => (
                        <button
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          data-testid={`quick-link-${item.id}`}
                          className="flex items-center gap-3 p-4 rounded-2xl transition-all text-left group hover:brightness-110"
                          style={{ background: item.bg, border: `1px solid ${item.border}` }}
                        >
                          <item.icon size={16} style={{ color: item.accent }} />
                          <span className="text-white font-semibold text-sm flex-1">{item.label}</span>
                          <ArrowRight size={12} className="text-zinc-700 group-hover:text-zinc-400 transition-colors" />
                        </button>
                      ))}
                    </div>

                    {/* Release einreichen */}
                    <LabelReleaseSubmitToggle labelName={label.name} publicApi={publicApi} />

                    {/* Hinweis */}
                    <div className="p-5 rounded-2xl bg-zinc-900/30 border border-white/[0.04]">
                      <div className="flex items-start gap-3">
                        <Clock size={14} className="text-zinc-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-zinc-400 text-sm font-semibold mb-0.5">{t("labels.studio.update_hint_title")}</p>
                          <p className="text-zinc-600 text-xs leading-relaxed">{t("labels.studio.update_hint_desc")}</p>
                        </div>
                      </div>
                    </div>

                    {/* Feedback */}
                    <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-zinc-900/20 border border-white/[0.03]">
                      <MessageCircle size={13} className="text-zinc-600 mt-0.5 flex-shrink-0" />
                      <p className="text-zinc-600 text-xs leading-relaxed">{t("labels.studio.feedback_note")}</p>
                    </div>
                  </div>
                )}

                {/* ── STATISTIK ──────────────────────────────────────────────── */}
                {activeTab === "stats" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-black text-white">{t("labels.studio.nav_stats")}</h2>
                      {!loadingStats && studioStats && (
                        <button
                          onClick={fetchStats}
                          className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition-colors px-3 py-1.5 rounded-xl hover:bg-white/[0.04]"
                        >
                          <TrendingUp size={12} /> Aktualisieren
                        </button>
                      )}
                    </div>

                    {loadingStats ? (
                      <div className="flex justify-center py-20">
                        <div className="w-8 h-8 border-2 border-emerald-500/40 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : !studioStats ? (
                      <div className="text-center py-20 space-y-4">
                        <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center"
                          style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.15)" }}>
                          <BarChart2 size={22} className="text-emerald-400" />
                        </div>
                        <p className="text-zinc-400 text-sm">{t("labels.studio.stats_no_data")}</p>
                        <button
                          onClick={fetchStats}
                          data-testid="stats-load-btn"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                          style={{ background: "rgba(16,185,129,0.2)", border: "1px solid rgba(16,185,129,0.3)" }}
                        >
                          <TrendingUp size={14} /> {t("labels.studio.stats_load_btn")}
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { val: studioStats.total_releases,     lbl: t("labels.studio.stat_releases"),  icon: Disc3,  color: "#34d399", bg: "rgba(16,185,129,0.08)"  },
                            { val: studioStats.releases_this_year, lbl: t("labels.studio.stat_this_year"), icon: Music,  color: "#60a5fa", bg: "rgba(59,130,246,0.08)"  },
                            { val: studioStats.total_votes,        lbl: t("labels.studio.stat_votes"),     icon: Heart,  color: "#f43f5e", bg: "rgba(244,63,94,0.08)"   },
                            { val: studioStats.total_hypes ?? 0,   lbl: t("labels.studio.stat_hypes"),     icon: Zap,    color: "#fbbf24", bg: "rgba(245,158,11,0.08)"  },
                          ].map(s => (
                            <div key={s.lbl} className="rounded-2xl p-4 flex flex-col gap-1"
                              style={{ background: s.bg, border: `1px solid ${s.color}22` }}>
                              <s.icon size={14} style={{ color: s.color }} className="mb-1" />
                              <p className="text-2xl font-black text-white leading-none">
                                {(s.val || 0).toLocaleString("de")}
                              </p>
                              <p className="text-[10px] uppercase tracking-wider font-semibold mt-0.5" style={{ color: s.color + "aa" }}>
                                {s.lbl}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* Zusätzliche Stats */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-xl p-4 bg-zinc-900/40 border border-white/[0.05]">
                            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">
                              {t("labels.studio.stat_releases")} {new Date().getFullYear() - 1}
                            </p>
                            <p className="text-2xl font-black text-white">{(studioStats.releases_last_year || 0).toLocaleString("de")}</p>
                          </div>
                          <div className="rounded-xl p-4 bg-zinc-900/40 border border-white/[0.05]">
                            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">{t("labels.studio.stats_pending_apps")}</p>
                            <p className="text-2xl font-black text-white">{studioStats.pending_apps || 0}</p>
                          </div>
                        </div>

                        {/* Releases pro Jahr – CSS Bar Chart */}
                        {studioStats.releases_by_year && (
                          <div className="rounded-2xl p-6 border border-white/[0.05]"
                            style={{ background: "rgba(0,0,0,0.25)" }}>
                            <p className="text-sm font-bold text-white mb-6">{t("labels.studio.stats_releases_by_year")}</p>
                            <div className="space-y-3">
                              {(() => {
                                const vals = Object.values(studioStats.releases_by_year);
                                const max = Math.max(...vals, 1);
                                return Object.entries(studioStats.releases_by_year).map(([year, count]) => (
                                  <div key={year} className="flex items-center gap-3">
                                    <span className="text-xs text-zinc-500 font-mono w-10 text-right flex-shrink-0">{year}</span>
                                    <div className="flex-1 h-7 rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.03)" }}>
                                      <div
                                        className="h-full rounded-lg transition-all duration-700"
                                        style={{
                                          width: `${Math.max((count / max) * 100, count > 0 ? 3 : 0)}%`,
                                          background: "linear-gradient(90deg, rgba(16,185,129,0.75) 0%, rgba(16,185,129,0.2) 100%)",
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs font-bold w-7 text-right flex-shrink-0" style={{ color: count > 0 ? "#d1d5db" : "#3f3f46" }}>{count}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Top 10 nach Votes */}
                        <div className="rounded-2xl border border-white/[0.05] overflow-hidden">
                          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.04]">
                            <Heart size={14} className="text-rose-500" />
                            <p className="text-white font-bold text-sm">{t("labels.studio.stats_top_by_votes")}</p>
                          </div>
                          {studioStats.top_by_votes?.length > 0 ? (
                            <div className="divide-y divide-white/[0.03]">
                              {studioStats.top_by_votes.map((r, i) => (
                                <a
                                  key={r.id}
                                  href={r.post_name ? `/release/${r.post_name}` : "#"}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.02] transition-colors"
                                >
                                  <span className="text-zinc-700 font-black text-sm w-5 flex-shrink-0">{i + 1}</span>
                                  {r.cover_url
                                    ? <img src={r.cover_url} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" onError={e => { e.target.style.display = "none"; }} />
                                    : <div className="w-9 h-9 rounded-xl bg-zinc-800 flex-shrink-0" />}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-semibold truncate">{r.interpret}</p>
                                    <p className="text-zinc-500 text-xs truncate">{r.titel}</p>
                                  </div>
                                  <div className="flex items-center gap-1 text-xs font-bold flex-shrink-0" style={{ color: "#f43f5e" }}>
                                    <Heart size={10} /> {(r.vote_count || 0).toLocaleString("de")}
                                  </div>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <div className="py-10 text-center text-zinc-600 text-sm">Noch keine Votes für deine Releases</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ── ARTISTS ────────────────────────────────────────────────── */}
                {activeTab === "artists" && (
                  <div className="space-y-5">
                    <h2 className="text-xl font-black text-white">{t("labels.studio.nav_artists")}</h2>

                    {/* Roster */}
                    <div className="rounded-2xl border border-white/[0.05] bg-zinc-900/30 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.04]">
                        <div className="w-8 h-8 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <Users size={14} className="text-blue-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{t("labels.studio.roster_title")}</p>
                          <p className="text-zinc-600 text-xs">{t("labels.studio.roster_count", { count: label.roster?.length || 0 })}</p>
                        </div>
                      </div>
                      <div className="px-5 pb-5 space-y-4 pt-4">
                        {(label.roster || []).length > 0 ? (
                          <div className="space-y-2">
                            {label.roster.map(a => (
                              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-zinc-800/60 border border-zinc-700">
                                <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-700 flex-shrink-0">
                                  {a.bild_url
                                    ? <img src={a.bild_url} alt="" className="w-full h-full object-cover" />
                                    : <span className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-400">{a.name?.[0]}</span>}
                                </div>
                                <a href={`/artist/${a.slug}`} target="_blank" rel="noreferrer"
                                  className="flex-1 text-sm font-medium text-zinc-200 hover:text-emerald-400 transition-colors">{a.name}</a>
                                <button onClick={() => removeFromRoster(a.id)}
                                  className="text-zinc-600 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10">
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-zinc-500 text-center py-4">{t("labels.studio.roster_empty")}</p>
                        )}
                        <div>
                          <p className="text-xs text-zinc-400 mb-2">{t("labels.studio.roster_invite_label")}</p>
                          <ArtistSearchAdd token={token} onAdded={fetchLabels} labelId={label?.id} />
                          <p className="text-xs text-zinc-600 mt-2">{t("labels.studio.roster_invite_note")}</p>
                        </div>
                      </div>
                    </div>

                    {/* Label Manager */}
                    {label.managers?.length > 0 && (
                      <div className="rounded-2xl border border-white/[0.05] bg-zinc-900/30 overflow-hidden">
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.04]">
                          <Shield size={14} className="text-emerald-400" />
                          <p className="text-white font-bold text-sm">{t("labels.studio.managers_title")}</p>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-400">{label.managers.length}</span>
                        </div>
                        <div className="p-3 space-y-1">
                          {label.managers.map(m => {
                            const isMe = m.auth0_id === (publicUser?.sub || publicUser?.auth0_id);
                            return (
                              <div key={m.auth0_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl">
                                <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0">
                                  {m.avatar_url
                                    ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" />
                                    : <span className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-500">{m.username?.[0]?.toUpperCase()}</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-zinc-200 font-medium truncate">{m.username}</p>
                                  {isMe ? (
                                    <select
                                      className="mt-0.5 text-xs bg-zinc-800 border border-white/10 text-emerald-400 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 cursor-pointer"
                                      value={m.role_title || "Label Manager"}
                                      onChange={async (e) => {
                                        try {
                                          await api.put("/labels/studio/my/role", { role: e.target.value, target_label_id: label?.id }, { headers: { Authorization: `Bearer ${token}` } });
                                          toast.success(t("labels.studio.role_saved"));
                                          fetchLabels();
                                        } catch { toast.error(t("common.error")); }
                                      }}
                                    >
                                      <option value="Label Manager">Label Manager</option>
                                      <option value="A&R Manager">A&amp;R Manager</option>
                                    </select>
                                  ) : (
                                    <p className="text-[10px] text-zinc-500 mt-0.5">{m.role_title || "Label Manager"}</p>
                                  )}
                                </div>
                                {isMe && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-bold">{t("labels.studio.you")}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── ANFRAGEN ───────────────────────────────────────────────── */}
                {activeTab === "requests" && (
                  <div className="space-y-5">
                    <h2 className="text-xl font-black text-white">{t("labels.studio.nav_requests")}</h2>
                    <LabelRequestsSection token={token} label={label} />
                  </div>
                )}

                {/* ── TOP RELEASES ───────────────────────────────────────────── */}
                {activeTab === "top-releases" && (
                  <div className="space-y-5">
                    <h2 className="text-xl font-black text-white">{t("labels.studio.nav_top_releases")}</h2>

                    <EditSection
                      title={t("labels.studio.top5_title", { count: top5.length })}
                      subtitle={t("labels.studio.top5_sub")}
                      icon={Music} iconColor="text-amber-400"
                      onSave={saveTop5} saving={savingTop5}
                    >
                      <div className="space-y-3">
                        {top5.length > 0 && (
                          <div className="space-y-2">
                            {top5.map((id, i) => {
                              const rel = label.top_releases_data?.find(r => r.id === id);
                              return (
                                <div key={id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-900 border border-zinc-800">
                                  <span className="text-xs text-zinc-600 w-4">{i + 1}</span>
                                  {rel?.cover_url && (
                                    <img src={rel.cover_url} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    {rel ? (
                                      <>
                                        <p className="text-white text-xs font-semibold truncate">{rel.interpret}</p>
                                        <p className="text-zinc-500 text-[10px] truncate">{rel.titel}</p>
                                      </>
                                    ) : (
                                      <span className="text-zinc-300 font-mono text-xs">{id}</span>
                                    )}
                                  </div>
                                  <button onClick={() => setTop5(ids => ids.filter(x => x !== id))}
                                    className="text-zinc-700 hover:text-red-400 transition p-1">
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {top5.length < 5 && (
                          <div className="relative">
                            <input value={top5Search} onChange={e => setTop5Search(e.target.value)}
                              placeholder={t("labels.studio.top5_search_ph")} className={inp} />
                            {top5Results.length > 0 && (
                              <div className="absolute top-full mt-2 left-0 right-0 z-50 bg-zinc-950/95 border border-white/10 rounded-2xl shadow-2xl max-h-64 overflow-y-auto">
                                {top5Results.map(r => (
                                  <button key={r.id} onClick={() => { setTop5(p => [...p, r.id]); setTop5Search(""); setTop5Results([]); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition text-left">
                                    {r.cover_url && <img src={r.cover_url} alt={r.titel} className="w-8 h-8 rounded-lg object-cover" />}
                                    <div>
                                      <p className="text-white text-sm">{r.interpret}</p>
                                      <p className="text-zinc-500 text-xs">{r.titel}</p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </EditSection>
                  </div>
                )}

                {/* ── PROFIL ─────────────────────────────────────────────────── */}
                {activeTab === "profile" && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-5 pb-6 border-b border-white/[0.06]">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-zinc-800 flex items-center justify-center flex-shrink-0 border border-zinc-700">
                        {label.logo_url
                          ? <img src={label.logo_url} alt={label.name} className="w-full h-full object-cover" />
                          : <Disc3 size={24} className="text-zinc-500" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-2xl font-black text-white">{label.name}</h2>
                          {label.is_verified && <CheckCircle2 size={16} className="text-emerald-400" title="Intern verifiziert" />}
                        </div>
                        <a href={`/label/${label.slug}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 mt-0.5 transition-colors">
                          <ExternalLink size={10} /> {t("labels.studio.public_profile")}
                        </a>
                      </div>
                    </div>

                    <EditSection title={t("labels.studio.save_profile_title")} subtitle={t("labels.studio.save_profile_sub")}
                      icon={Edit2} iconColor="text-rose-400" onSave={saveProfile} saving={savingProfile}>
                      <div className="space-y-3">
                        <div>
                          <p className="text-zinc-600 text-xs mb-3">
                            Empfehlung: <span className="text-zinc-400 font-semibold">1200 × 320 px</span> &nbsp;·&nbsp; breit und flach (ca. 4:1) &nbsp;·&nbsp; max. 8 MB &nbsp;·&nbsp; JPEG, PNG oder WebP
                          </p>
                          <ImageUploadField
                            value={profileForm.banner_url || ""}
                            onChange={v => setProfileForm(f => ({ ...f, banner_url: v }))}
                            uploadEndpoint="/media/label-banner"
                            oldPendingUrl=""
                            label={t("labels.studio.field_banner")}
                            testId="label-banner"
                            variant="banner"
                          />
                        </div>
                        <div>
                          <ImageUploadField
                            value={profileForm.logo_url || ""}
                            onChange={v => setProfileForm(f => ({ ...f, logo_url: v }))}
                            uploadEndpoint="/media/label-image"
                            oldPendingUrl=""
                            label={t("labels.studio.field_logo")}
                            testId="label-logo"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-zinc-400 mb-1 block">{t("labels.studio.field_bio")}</label>
                          <textarea className={`${inp} resize-none`} rows={4} value={profileForm.bio}
                            onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))} placeholder={t("labels.studio.field_bio_ph")} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-zinc-400 mb-1 block">{t("labels.studio.field_founded")}</label>
                            <input type="number" className={inp} value={profileForm.founded_year}
                              onChange={e => setProfileForm(f => ({ ...f, founded_year: e.target.value ? +e.target.value : null }))} placeholder="2008" />
                          </div>
                          <div>
                            <label className="text-xs text-zinc-400 mb-1 block">{t("labels.studio.field_country")}</label>
                            <input className={inp} value={profileForm.country}
                              onChange={e => setProfileForm(f => ({ ...f, country: e.target.value }))} placeholder="DE" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1.5">
                            <Mail size={11} className="text-emerald-500" /> Demo-Kontakt E-Mail
                          </label>
                          <input type="email" className={inp} value={profileForm.contact_email || ""}
                            onChange={e => setProfileForm(f => ({ ...f, contact_email: e.target.value }))}
                            placeholder="demos@meinlabel.com" />
                          <p className="text-[10px] text-zinc-600 mt-1">
                            Wird im "Demo einreichen"-Bereich als Kontaktadresse angezeigt
                          </p>
                        </div>                        <div className="space-y-2">
                          <label className="text-xs text-zinc-400 block">{t("labels.studio.field_social")}</label>
                          {[
                            { key: "website",    label: "Website",     ph: "https://meinlabel.com" },
                            { key: "instagram",  label: "Instagram",   ph: "https://instagram.com/meinlabel" },
                            { key: "facebook",   label: "Facebook",    ph: "https://facebook.com/meinlabel" },
                            { key: "x",          label: "X / Twitter", ph: "https://x.com/meinlabel" },
                            { key: "tiktok",     label: "TikTok",      ph: "https://tiktok.com/@meinlabel" },
                            { key: "beatport",   label: "Beatport",    ph: "https://beatport.com/label/..." },
                            { key: "spotify",    label: "Spotify",     ph: "https://open.spotify.com/user/..." },
                            { key: "soundcloud", label: "SoundCloud",  ph: "https://soundcloud.com/meinlabel" },
                            { key: "youtube",    label: "YouTube",     ph: "https://youtube.com/@meinlabel" },
                          ].map(({ key, label: lbl, ph }) => (
                            <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "7rem 1fr" }}>
                              <span className="text-zinc-500 text-xs text-right">{lbl}</span>
                              <input className={inp} placeholder={ph}
                                value={profileForm.social_links?.[key] || ""}
                                onChange={e => setProfileForm(f => ({ ...f, social_links: { ...f.social_links, [key]: e.target.value } }))} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </EditSection>
                  </div>
                )}

              </div>
            </div>
          </>
        )}

      </main>
      <Footer />
    </div>
  );
}
